import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';

const BibleStudyBulkUpload = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  // 파일 선택 핸들러
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  // 날짜 변환 헬퍼 함수 (YYYY-MM-DD -> YYYYMMDD)
  const toDbDate = (value) => {
    if (!value) return null;
    if (!value.includes('-')) return value;
    return value.replace(/-/g, '');
  };

  // 빈 문자열을 null로 변환하는 헬퍼 함수
  const toNullIfEmpty = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    return value;
  };

  // 파일 업로드 및 파싱
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      alert('파일을 선택하세요.');
      return;
    }
    setUploading(true);
    setResult(null);

    try {
      const text = await file.text();
      let jsonData;
      try {
        jsonData = JSON.parse(text);
      } catch (err) {
        alert('JSON 파싱 오류: 파일이 올바른 JSON 형식인지 확인하세요.');
        setUploading(false);
        return;
      }

      if (!Array.isArray(jsonData)) {
        alert('JSON 파일은 배열 형식이어야 합니다.');
        setUploading(false);
        return;
      }

      let totalStudies = 0;
      let totalQuestions = 0;
      const errors = [];

      // 각 성경공부 항목을 순차적으로 처리 (형식: bible_study_sample.json)
      for (const study of jsonData) {
        try {
          // 필수 필드 검증 — 샘플과 동일: study_date, title, scripture_reference, scripture_text
          if (!study.study_date || !study.title || !study.scripture_reference || !study.scripture_text) {
            const errorMsg = `[${study.study_date || '알 수 없음'}] 필수 항목(study_date, title, scripture_reference, scripture_text)이 누락되었습니다.`;
            errors.push(errorMsg);
            continue;
          }

          // 1. bible_study 테이블에 삽입
          const studyPayload = {
            title: study.title.trim(),
            subtitle: toNullIfEmpty(study.subtitle),
            hymn: toNullIfEmpty(study.hymn),
            grace_sharing_text: toNullIfEmpty(study.grace_sharing_text),
            scripture_reference: study.scripture_reference.trim(),
            scripture_text: study.scripture_text.trim(),
            study_date: toDbDate(study.study_date),
            is_active: study.is_active, // 기본값 true
            country_cd: study.country_cd || 'KR',
            group_cd: study.group_cd || '100',
            language: study.language || 'ko',
          };

          const { data: studyData, error: studyError } = await supabase
            .from('bible_study')
            .insert(studyPayload)
            .select()
            .single();

          if (studyError) {
            const errorMsg = `[${study.study_date || study.title || '알 수 없음'}] 성경공부 삽입 오류: ${studyError.message}`;
            console.error(errorMsg);
            errors.push(errorMsg);
            continue;
          }

          const studyId = studyData.id;
          totalStudies++;

          // 2. bible_study_q 테이블에 삽입 (questions[].seq, question, reference, reference_text, closing_text)
          if (study.questions && Array.isArray(study.questions) && study.questions.length > 0) {
            const sorted = [...study.questions].sort(
              (a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0)
            );
            const questionsToInsert = sorted
              .filter((q) => q.question && String(q.question).trim() !== '')
              .map((question, index) => ({
                study_id: studyId,
                seq: Number(question.seq) > 0 ? Number(question.seq) : index + 1,
                question: String(question.question).trim(),
                reference: toNullIfEmpty(question.reference),
                reference_text: toNullIfEmpty(question.reference_text),
                closing_text: toNullIfEmpty(question.closing_text),
              }));

            if (questionsToInsert.length > 0) {
              const { error: questionsError } = await supabase
                .from('bible_study_q')
                .insert(questionsToInsert);

              if (questionsError) {
                const errorMsg = `[${study.study_date || study.title || '알 수 없음'}] 질문 삽입 오류: ${questionsError.message}`;
                console.error(errorMsg);
                errors.push(errorMsg);
              } else {
                totalQuestions += questionsToInsert.length;
              }
            }
          }
        } catch (err) {
          const errorMsg = `[${study?.study_date || study?.title || '알 수 없음'}] 처리 오류: ${err.message}`;
          console.error(errorMsg);
          errors.push(errorMsg);
        }
      }

      // 결과 메시지 생성
      let message = '';
      if (errors.length > 0) {
        message = `업로드 완료: 성경공부 ${totalStudies}건, 질문 ${totalQuestions}건\n\n오류 발생: ${errors.length}건\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n... 외 ${errors.length - 5}건` : ''}`;
      } else {
        message = `성공적으로 업로드되었습니다. 성경공부: ${totalStudies}건, 질문: ${totalQuestions}건`;
        if (onSuccess) onSuccess();
      }

      setResult({ 
        success: errors.length === 0, 
        message: message
      });
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">성경공부 일괄 입력</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label>JSON 텍스트 파일 선택</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".json,.txt,application/json,text/plain"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </div>
              <div className="d-flex justify-content-end">
                <button type="button" className="btn btn-secondary mr-2" onClick={onClose} disabled={uploading}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploading || !file}>
                  {uploading ? '업로드 중...' : '업로드'}
                </button>
              </div>
            </form>
            {result && (
              <div className={`mt-3 alert ${result.success ? 'alert-success' : 'alert-danger'}`}>
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{result.message}</pre>
              </div>
            )}
            <div className="mt-3">
              <small>
                <b>※ 파일 형식</b> (<code>bible_study_sample.json</code> 참고)<br />
                최상위는 배열이며, 각 요소는 성경공부 1회 분량입니다.<br />
                <br />
                <strong>성경공부 본문</strong>: <code>study_date</code>(YYYY-MM-DD 또는 YYYYMMDD),{' '}
                <code>title</code>, <code>scripture_reference</code>, <code>scripture_text</code> 필수 ·{' '}
                <code>subtitle</code>, <code>hymn</code>, <code>grace_sharing_text</code> 선택<br />
                <br />
                <strong>질문</strong> <code>questions</code>: 각 항목에 <code>seq</code>, <code>question</code> 필수 ·{' '}
                <code>reference</code>(참고 구절 표기), <code>reference_text</code>(참고구절 본문),{' '}
                <code>closing_text</code>(마무리 문구) 선택<br />
                <br />
                동일 <code>study_date</code>가 DB에 이미 있으면 UNIQUE 제약으로 삽입이 실패할 수 있습니다.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BibleStudyBulkUpload;